import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import SessionWarningListener from './components/SessionWarningListener/SessionWarningListener';
import SiteHeader from './components/Layout/SiteHeader';
import GlobalFooter from './components/footer/GlobalFooter';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './App.css';

//  Lazy-loaded pages (Section 20 — code splitting)
// Auth
const Login                 = React.lazy(() => import('./pages/Auth/Login'));
const Register              = React.lazy(() => import('./pages/Auth/Register'));
const VerifyEmail           = React.lazy(() => import('./pages/Auth/VerifyEmail'));
const VerificationCenter    = React.lazy(() => import('./pages/Auth/VerificationCenter'));
const EmailVerificationPending = React.lazy(() => import('./pages/Auth/EmailVerificationPending'));
const ForgotPassword        = React.lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword         = React.lazy(() => import('./pages/Auth/ResetPassword'));

// Main protected pages
const Dashboard             = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Accounts              = React.lazy(() => import('./pages/Accounts/Accounts'));
const AccountDetail         = React.lazy(() => import('./pages/Accounts/AccountDetail'));
const Transactions          = React.lazy(() => import('./pages/Transactions/Transactions'));
const Transfer              = React.lazy(() => import('./pages/Transactions/Transfer'));
const TransferConfirm       = React.lazy(() => import('./pages/Transactions/TransferConfirm'));
const ReceiptView           = React.lazy(() => import('./pages/Transactions/ReceiptView'));
const Loans                 = React.lazy(() => import('./pages/Loans/Loans'));
const LoanDetail            = React.lazy(() => import('./pages/Loans/LoanDetail'));
const LoanApplication       = React.lazy(() => import('./pages/Loans/LoanApplication'));
const BusinessLoanApplication = React.lazy(() => import('./pages/Loans/BusinessLoanApplication'));
const LoanCalculator        = React.lazy(() => import('./pages/Loans/LoanCalculator'));
const LoanHistory           = React.lazy(() => import('./pages/Loans/LoanHistory'));
const Bills                 = React.lazy(() => import('./pages/Bills/Bills'));
const BillPayment           = React.lazy(() => import('./pages/Bills/BillPayment'));
const Orders                = React.lazy(() => import('./pages/Orders/Orders'));
const OrderDetail           = React.lazy(() => import('./pages/Orders/OrderDetail'));
const CreateOrder           = React.lazy(() => import('./pages/Orders/CreateOrder'));
const Vendors               = React.lazy(() => import('./pages/Orders/Vendors'));
const Profile               = React.lazy(() => import('./pages/Profile/Profile'));
const Support               = React.lazy(() => import('./pages/Support/Support'));
const Business              = React.lazy(() => import('./pages/Business/Business'));
const Treasury              = React.lazy(() => import('./pages/Treasury/Treasury'));
const Cards                 = React.lazy(() => import('./pages/Cards/Cards'));
const FX                    = React.lazy(() => import('./pages/FX/FX'));
const Notifications         = React.lazy(() => import('./pages/Notifications/Notifications'));
const DocumentsTab          = React.lazy(() => import('./pages/Documents/DocumentsTab'));
const VerifyDocument        = React.lazy(() => import('./pages/Documents/VerifyDocument'));

// Public marketing pages
const LandingHome           = React.lazy(() => import('./pages/Landing/LandingHome'));
const About                 = React.lazy(() => import('./pages/About/About'));
const FAQ                   = React.lazy(() => import('./pages/FAQ/FAQ'));
const Contact               = React.lazy(() => import('./pages/Contact/Contact'));
const Terms                 = React.lazy(() => import('./pages/Terms/Terms'));
const Privacy               = React.lazy(() => import('./pages/Privacy/Privacy'));
const ServicesPage          = React.lazy(() => import('./pages/Public/ServicesPage'));
const SecurityPage          = React.lazy(() => import('./pages/Public/SecurityPage'));
const SecurityCenter        = React.lazy(() => import('./pages/Public/SecurityCenter'));
const SupportCenter         = React.lazy(() => import('./pages/Public/SupportCenter'));
const CompliancePage        = React.lazy(() => import('./pages/Public/CompliancePage'));
const Operations            = React.lazy(() => import('./pages/Operations/Operations'));
const OpsLogin              = React.lazy(() => import('./pages/Operations/OpsLogin'));
const EmailOperations       = React.lazy(() => import('./pages/EmailOperations/EmailOperations'));
const StaffDashboard        = React.lazy(() => import('./pages/StaffDashboard/StaffDashboard'));

// Security Pages (footer links)
const FraudPreventionPage    = React.lazy(() => import('./pages/Public/FraudPreventionPage'));
const ReportFraudPage       = React.lazy(() => import('./pages/Public/ReportFraudPage'));
const SecurityAlertsPage    = React.lazy(() => import('./pages/Public/SecurityAlertsPage'));
const TwoFactorPage         = React.lazy(() => import('./pages/Public/TwoFactorPage'));
const SafetyTipsPage        = React.lazy(() => import('./pages/Public/SafetyTipsPage'));
const IdentityTheftPage     = React.lazy(() => import('./pages/Public/IdentityTheftPage'));
const PhishingPage          = React.lazy(() => import('./pages/Public/PhishingPage'));

// Legal Pages (footer links)
const CookiePolicy          = React.lazy(() => import('./pages/Legal/CookiePolicy'));
const EcdPage               = React.lazy(() => import('./pages/Legal/EcdPage'));
const AccessibilityPage     = React.lazy(() => import('./pages/Legal/AccessibilityPage'));
const IpNoticePage          = React.lazy(() => import('./pages/Legal/IpNoticePage'));
const TaxDocsPage           = React.lazy(() => import('./pages/Legal/TaxDocsPage'));
const AnnualReportPage      = React.lazy(() => import('./pages/Legal/AnnualReportPage'));
const WhistleblowerPage     = React.lazy(() => import('./pages/Legal/WhistleblowerPage'));
const ModernSlaveryPage     = React.lazy(() => import('./pages/Legal/ModernSlaveryPage'));

//  Page loading fallback
const PageSpinner = () => (
  <div
    className="loading-spinner"
    role="status"
    aria-label="Loading page"
    style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <span className="sr-only">Loading…</span>
  </div>
);

// Enforce light mode for all public pages — prevents dark-mode bleed from authenticated session
const useLightMode = () => {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    document.documentElement.style.colorScheme = 'light';
  }, []);
};

// Restore user's stored theme for authenticated pages (in case Layout hasn't mounted yet)
const useRestoreTheme = () => {
  useEffect(() => {
    try {
      const stored = localStorage.getItem('pww_theme');
      const theme = stored === 'dark' ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', theme);
      document.documentElement.style.colorScheme = theme;
    } catch {
      // ignore
    }
  }, []);
};

// Public Route Component (for pages that don't require auth)
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();
  useLightMode();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  useRestoreTheme();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const VerifiedDashboardRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  useRestoreTheme();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.is_staff || user?.is_superuser) return children;
  const verified = user?.verified_by_admin || ['verified', 'fully_verified'].includes(user?.verification_status);
  if (!user?.email_verified || !verified) {
    return <Navigate to="/verification-center" replace />;
  }

  return children;
};

const VerificationCenterRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.is_staff || user?.is_superuser) return <Navigate to="/dashboard" replace />;
  return children;
};

// Staff-only route — redirects to /operations/login if not authenticated as staff
const StaffRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) return <Navigate to="/operations/login" />;
  if (!user?.is_staff) return <Navigate to="/operations/login" />;
  return children;
};

// Landing route for the public homepage
const LandingRoute = () => {
  return (
    <PublicLayout>
      <LandingHome />
    </PublicLayout>
  );
};

// Layout with Sidebar — neutral shell; Layout.js inside each page provides the
// fixed sidebar + header + padded content area.  Do NOT add padding or
// max-width here or it creates a gap between the fixed sidebar and content.
const DashboardLayout = ({ children }) => {
  return (
    <div className="dashboard-shell">
      {children}
    </div>
  );
};

// Layout for Public Pages — always enforces light mode
const PublicLayout = ({ children }) => {
  useLightMode();
  return (
    <div className="public-app">
      <SiteHeader />
      <main className="public-content">
        {children}
      </main>
      <GlobalFooter />
    </div>
  );
};

// Auth pages (login, register) — always light mode, no chrome
const AuthLayout = ({ children }) => {
  useLightMode();
  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <Suspense fallback={<PageSpinner />}>
              <AppRoutes />
            </Suspense>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  return (
    <>
      <SessionWarningListener />
      <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingRoute />} />
          <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />
          <Route path="/register" element={<AuthLayout><Register /></AuthLayout>} />
          <Route path="/verify-email" element={<AuthLayout><PublicRoute><VerifyEmail /></PublicRoute></AuthLayout>} />
          <Route path="/verify-email/:token" element={<AuthLayout><PublicRoute><VerifyEmail /></PublicRoute></AuthLayout>} />
          <Route path="/verification-center" element={<VerificationCenterRoute><DashboardLayout><VerificationCenter /></DashboardLayout></VerificationCenterRoute>} />
          <Route path="/verify-email-code" element={<AuthLayout><EmailVerificationPending /></AuthLayout>} />
          <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
          <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<PublicRoute><PublicLayout><About /></PublicLayout></PublicRoute>} />
          <Route path="/faq" element={<PublicRoute><PublicLayout><FAQ /></PublicLayout></PublicRoute>} />
          <Route path="/contact" element={<PublicRoute><PublicLayout><Contact /></PublicLayout></PublicRoute>} />
          <Route path="/services" element={<PublicRoute><PublicLayout><ServicesPage /></PublicLayout></PublicRoute>} />
          <Route path="/terms" element={<PublicRoute><PublicLayout><Terms /></PublicLayout></PublicRoute>} />
          <Route path="/privacy" element={<PublicRoute><PublicLayout><Privacy /></PublicLayout></PublicRoute>} />
          <Route path="/security" element={<PublicRoute><PublicLayout><SecurityPage /></PublicLayout></PublicRoute>} />
          <Route path="/security-center" element={<PublicRoute><PublicLayout><SecurityCenter /></PublicLayout></PublicRoute>} />
          <Route path="/support-center" element={<PublicRoute><PublicLayout><SupportCenter /></PublicLayout></PublicRoute>} />
          <Route path="/compliance" element={<PublicRoute><PublicLayout><CompliancePage /></PublicLayout></PublicRoute>} />

          {/* Legacy public marketing routes now point to the client portal/support flow. */}
          <Route path="/checking"       element={<Navigate to="/login" replace />} />
          <Route path="/savings"        element={<Navigate to="/login" replace />} />
          <Route path="/credit-cards"   element={<Navigate to="/login" replace />} />
          <Route path="/home-loans"     element={<Navigate to="/login" replace />} />
          <Route path="/auto"           element={<Navigate to="/support-center" replace />} />
          <Route path="/invest"         element={<Navigate to="/support-center" replace />} />
          <Route path="/education"      element={<Navigate to="/support-center" replace />} />
          <Route path="/travel"         element={<Navigate to="/support-center" replace />} />
          <Route path="/student-loans"  element={<Navigate to="/support-center" replace />} />
          <Route path="/business-loans" element={<Navigate to="/support-center" replace />} />
          <Route path="/personal"       element={<Navigate to="/login" replace />} />
          <Route path="/investments"    element={<Navigate to="/support-center" replace />} />

          {/* Security Footer Pages */}
          <Route path="/security/fraud-prevention"  element={<PublicRoute><PublicLayout><FraudPreventionPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/report-fraud"      element={<PublicRoute><PublicLayout><ReportFraudPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/alerts"            element={<PublicRoute><PublicLayout><SecurityAlertsPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/2fa"               element={<PublicRoute><PublicLayout><TwoFactorPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/safety-tips"       element={<PublicRoute><PublicLayout><SafetyTipsPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/identity-theft"    element={<PublicRoute><PublicLayout><IdentityTheftPage /></PublicLayout></PublicRoute>} />
          <Route path="/security/phishing"          element={<PublicRoute><PublicLayout><PhishingPage /></PublicLayout></PublicRoute>} />

          {/* Legal Footer Pages */}
          <Route path="/cookies"                     element={<PublicRoute><PublicLayout><CookiePolicy /></PublicLayout></PublicRoute>} />
          <Route path="/legal/ecd"                   element={<PublicRoute><PublicLayout><EcdPage /></PublicLayout></PublicRoute>} />
          <Route path="/accessibility"               element={<PublicRoute><PublicLayout><AccessibilityPage /></PublicLayout></PublicRoute>} />
          <Route path="/legal/ip-notice"             element={<PublicRoute><PublicLayout><IpNoticePage /></PublicLayout></PublicRoute>} />
          <Route path="/legal/tax-docs"              element={<PublicRoute><PublicLayout><TaxDocsPage /></PublicLayout></PublicRoute>} />
          <Route path="/legal/annual-report"         element={<PublicRoute><PublicLayout><AnnualReportPage /></PublicLayout></PublicRoute>} />
          <Route path="/legal/whistleblower"         element={<PublicRoute><PublicLayout><WhistleblowerPage /></PublicLayout></PublicRoute>} />
          <Route path="/legal/modern-slavery"        element={<PublicRoute><PublicLayout><ModernSlaveryPage /></PublicLayout></PublicRoute>} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Accounts />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AccountDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Transactions />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions/:id/receipt"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReceiptView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Support />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfer"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Transfer />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfer/confirm/:transactionId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TransferConfirm />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Loans />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LoanDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/apply"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LoanApplication />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/apply/business"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BusinessLoanApplication />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/calculator"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LoanCalculator />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans/history"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LoanHistory />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Bills />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/pay"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BillPayment />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/:id/pay"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BillPayment />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Orders />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OrderDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/create"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CreateOrder />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Vendors />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/business"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Business />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cards"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Cards />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/treasury"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Treasury />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fx"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FX />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Profile />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Notifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DocumentsTab />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify/:docId"
            element={<VerifyDocument />}
          />

          {/* Bank Operations Console — staff only, own full-screen layout */}
          <Route
            path="/operations/login"
            element={<OpsLogin />}
          />
          <Route
            path="/operations"
            element={
              <StaffRoute>
                <Operations />
              </StaffRoute>
            }
          />
          <Route
            path="/email-center"
            element={
              <StaffRoute>
                <EmailOperations />
              </StaffRoute>
            }
          />

          {/* Staff Operations Dashboard — staff only */}
          <Route
            path="/staff"
            element={
              <StaffRoute>
                <StaffDashboard />
              </StaffRoute>
            }
          />

          {/* Redirect */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </>
    );
}

export default App;
